import { useMemo, useState } from 'react';
import { App, Button, Card, Drawer, Form, Input, Popconfirm, Space, Table, Typography } from 'antd';
import { CopyOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { EmailDesign, TemplateItem } from '@crm/shared';
import { defaultEmailDesign, designFromHtml } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { EmailDesigner } from '../../components/email-designer/EmailDesigner';
import { AiEmailButtons } from '../../components/email-designer/AiEmailButtons';
import { useAgents } from '../../features/email/api';
import { useSettings } from '../../features/settings/api';
import { AgentSelect } from '../../components/AgentSelect';
import { errorMessage } from '../../api/client';
import { useCreateTemplate, useDeleteTemplate, useDuplicateTemplate, useTemplates, useUpdateTemplate } from '../../features/campaigns/api';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { fmtDateTime } from '../../utils/format';

export function TemplatesPage() {
  const { message } = App.useApp();
  const templates = useTemplates();
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const remove = useDeleteTemplate();
  const duplicate = useDuplicateTemplate();
  const [editing, setEditing] = useState<TemplateItem | 'new' | null>(null);
  const [design, setDesign] = useState<EmailDesign>(() => defaultEmailDesign());
  const [form] = Form.useForm<{ name: string; subject: string; agentId?: string | null }>();
  const agentId = Form.useWatch('agentId', form);
  const subject = Form.useWatch('subject', form);
  const agents = useAgents();
  const settings = useSettings();
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const needle = q.toLowerCase();
    return (templates.data ?? []).filter((t) => !needle || `${t.name} ${t.subject}`.toLowerCase().includes(needle));
  }, [templates.data, q]);

  const open = (t: TemplateItem | 'new') => {
    form.resetFields();
    if (t !== 'new') form.setFieldsValue({ name: t.name, subject: t.subject, agentId: t.agentId });
    const s = settings.data;
    const brand = s ? { logoUrl: s.brandLogoFileId ? `/api/files/${s.brandLogoFileId}` : '', footerText: s.companyName ? `You are receiving this email from ${s.companyName}.` : '' } : {};
    setDesign(t === 'new' ? defaultEmailDesign(brand) : (t.design ?? designFromHtml(t.html, brand)));
    setEditing(t);
  };
  const save = async (v: { name: string; subject: string; agentId?: string | null }) => {
    if (!design.blocks.length) return message.warning('Add at least one block to the email');
    try {
      if (editing === 'new') await create.mutateAsync({ ...v, design });
      else if (editing) await update.mutateAsync({ id: editing.id, ...v, design });
      message.success(editing === 'new' ? 'Template created' : 'Template saved');
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page">
      <PageHeader title="Templates" subtitle="Reusable email content for campaigns. Merge tags are filled per contact at send time." extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>New template</Button>} />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={q} onSearch={setQ} placeholder="Search templates" />
        </div>
        <div className="toolbar-group">
          <Typography.Text type="secondary">{templates.data ? `${rows.length} of ${templates.data.length}` : ''}</Typography.Text>
        </div>
      </div>
      <Table<TemplateItem>
        rowKey="id"
        loading={tableLoading(templates)}
        locale={tableLocale(
          templates,
          q ? (
            <EmptyState description={`No templates match "${q}".`} action={<Button onClick={() => setQ('')}>Clear search</Button>} />
          ) : (
            <EmptyState description="No templates yet. Save an email you reuse across campaigns." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>New template</Button>} />
          ),
        )}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 820 }}
        columns={[
          {
            title: 'Template',
            dataIndex: 'name',
            render: (v: string, t) => (
              <button type="button" className="cell-button" onClick={() => open(t)} aria-label={`Edit template ${v}`} style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%', font: 'inherit' }}>
                <NameCell icon={<FileTextOutlined />} name={v} sub={t.subject} />
              </button>
            ),
          },
          { title: 'Agent', key: 'agent', width: 160, render: (_, t) => t.agent?.name ?? <Typography.Text type="secondary">Any agent</Typography.Text> },
          { title: 'Updated', dataIndex: 'updatedAt', width: 160, render: (v) => fmtDateTime(v) },
          {
            key: 'actions',
            align: 'right',
            render: (_, t) => (
              <Space>
                <Button size="small" icon={<CopyOutlined />} loading={duplicate.isPending && duplicate.variables === t.id} onClick={() => duplicate.mutateAsync(t.id).then(() => message.success('Template duplicated')).catch((e) => message.error(errorMessage(e)))}>Duplicate</Button>
                <Popconfirm title={`Delete template "${t.name}"?`} okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(t.id).then(() => message.success('Template deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Drawer
        title={editing === 'new' ? 'New template' : `Edit ${editing?.name ?? ''}`}
        open={!!editing}
        onClose={() => setEditing(null)}
        size={1320}
        destroyOnHidden
        extra={<Space><Button onClick={() => setEditing(null)}>Cancel</Button><Button type="primary" onClick={() => form.submit()} loading={create.isPending || update.isPending}>Save</Button></Space>}
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={save}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
            <Form.Item name="subject" label="Subject" rules={[{ required: true }]}><Input placeholder="Hello {{first_name|there}}" /></Form.Item>
            <Form.Item name="agentId" label="Default sender"><AgentSelect /></Form.Item>
          </div>
        </Form>
        <div style={{ marginBottom: 16 }}>
          <AiEmailButtons subject={subject ?? ''} design={design} agentId={agentId} onApply={(next) => { form.setFieldValue('subject', next.subject); setDesign(next.design); }} />
        </div>
        <EmailDesigner design={design} onChange={setDesign} agent={agents.data?.find((a) => a.id === agentId) ?? null} />
      </Drawer>
    </div>
  );
}
